"use client";

import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey } from "lexical";
import {
  FiFilm,
  FiFileText,
  FiImage,
  FiLayers,
  FiMusic,
  FiUser,
} from "react-icons/fi";
import { FaPause, FaPlay, FaStop } from "react-icons/fa";
import { TbMoodSmile } from "react-icons/tb";
import { LuLoaderCircle, LuSparkles, LuMapPin, LuBox } from "react-icons/lu";

import { ArtifactScope } from "@kagami/shared/artifactScope";
import { useShow } from "@/contexts/ShowContext";
import { useModal } from "@/contexts/ModalContext";
import { useScriptAssets } from "@/contexts/ScriptAssetsContext";
import { useEditorAssets } from "./EditorAssetsContext";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/shadcn/tooltip";
import AssetPreviewModal from "@/components/modals/AssetPreviewModal";
import useKagamiBridge from "@/hooks/useKagamiBridge";
import { apiFetch } from "@/lib/api";
import { $extractSpeechAfterMention } from "./scriptExtraction";

const VOICE_MODEL_ID = "698476656cbae33f09763f19";

// Lets a host editor opt into compact chips. The script editor leaves this at
// the default (rich character chip with thumbnail + voice controls); the prompt
// input sets `compactCharacter` so mentions stay small and inline.
export const MentionChipContext = createContext({ compactCharacter: false });

const COMMAND_STYLES = {
  segment: {
    bg: "bg-primary/10",
    border: "border-primary/40",
    text: "text-primary",
  },
  scene: {
    bg: "bg-secondary/20",
    border: "border-secondary-foreground/40",
    text: "text-secondary-foreground",
  },
  description: {
    bg: "bg-sky-500/20",
    border: "border-sky-500/40",
    text: "text-sky-300",
  },
  break: {
    bg: "bg-orange-500/20",
    border: "border-orange-500/40",
    text: "text-orange-300",
  },
  emotion: {
    bg: "bg-pink-500/20",
    border: "border-pink-500/40",
    text: "text-pink-300",
  },
};

const CHARACTER_STYLE = {
  bg: "bg-indigo-500/20",
  border: "border-indigo-500/40",
  text: "text-indigo-300",
};

const DEFAULT_STYLE = {
  bg: "bg-amber-500/20",
  border: "border-amber-500/40",
  text: "text-amber-300",
};

const ARTIFACT_STYLE = {
  bg: "bg-emerald-500/20",
  border: "border-emerald-500/40",
  text: "text-emerald-300",
};

const LOCATION_STYLE = {
  bg: "bg-teal-500/20",
  border: "border-teal-500/40",
  text: "text-teal-300",
};

const PROP_STYLE = {
  bg: "bg-fuchsia-500/20",
  border: "border-fuchsia-500/40",
  text: "text-fuchsia-300",
};

const ASSET_STYLE = {
  bg: "bg-red-500/20",
  border: "border-red-500/40",
  text: "text-red-300",
};

const isCharacterArtifact = (mentionData) =>
  mentionData?.scope === ArtifactScope.Character;
const isLocationArtifact = (mentionData) =>
  mentionData?.scope === ArtifactScope.Location;
const isPropArtifact = (mentionData) =>
  mentionData?.scope === ArtifactScope.Prop;

function getStyle(mentionType, mentionId, mentionData) {
  if (mentionType === "character") return CHARACTER_STYLE;
  if (mentionType === "artifact") {
    if (isCharacterArtifact(mentionData)) return CHARACTER_STYLE;
    if (isLocationArtifact(mentionData)) return LOCATION_STYLE;
    if (isPropArtifact(mentionData)) return PROP_STYLE;
    return ARTIFACT_STYLE;
  }
  if (mentionType === "asset") return ASSET_STYLE;
  return COMMAND_STYLES[mentionId] || DEFAULT_STYLE;
}

// Mention chips render as `contentEditable={false}` islands, so the browser's
// native text-selection highlight never paints across them the way it does for
// surrounding text. This tracks whether the current selection spans this node
// so each chip can draw its own indicator instead.
//
// Deliberately reads the native DOM selection directly (via `selectionchange`
// + `Range.intersectsNode`) rather than going through Lexical's `$getSelection()`
// on `SELECTION_CHANGE_COMMAND`: Lexical skips its own selection reconciliation
// when a click lands in/near decorator content it doesn't recognize as "within
// the editor" (`isSelectionCapturedInDecoratorInput` — exactly what BreakChip's
// and EmotionChip's own `<input>`s are), which left `$getSelection()` stuck on
// the old selection and the chip stuck highlighted. The native selection is
// never subject to that — a click always collapses it immediately.
function useMentionSelected(editor, nodeKey) {
  const [selected, setSelected] = useState(false);

  useEffect(() => {
    const updateSelected = () => {
      const domSelection = window.getSelection();
      if (
        !domSelection ||
        domSelection.rangeCount === 0 ||
        domSelection.isCollapsed
      ) {
        setSelected(false);
        return;
      }
      const element = editor.getElementByKey(nodeKey);
      setSelected(
        !!element && domSelection.getRangeAt(0).intersectsNode(element),
      );
    };
    updateSelected();
    document.addEventListener("selectionchange", updateSelected);
    return () =>
      document.removeEventListener("selectionchange", updateSelected);
  }, [editor, nodeKey]);

  return selected;
}

// Matches the browser's own text-selection background (rather than a guessed
// blue) so a selected chip reads as part of the same selection as the text
// around it. Text color forces to the same `text-secondary-foreground` color
// regular prose keeps while selected — `var(--secondary-foreground)` is that
// class's own CSS variable, so this stays in sync with the theme instead of
// letting the chip's own accent color (e.g. text-indigo-300) show through.
// The border matches the background so it blends into one solid block rather
// than outlining it.
const selectionRingStyle = (selected) =>
  selected
    ? {
        backgroundColor: "Highlight",
        borderColor: "Highlight",
        color: "var(--secondary-foreground)",
      }
    : undefined;

function SegmentChip({ style, selected }) {
  return (
    <span
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center px-1.5 py-1 mx-0.5 rounded
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
      contentEditable={false}>
      <FiLayers className="w-3.5 h-3.5" />
    </span>
  );
}

function SceneChip({ style, selected }) {
  return (
    <span
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center px-1.5 py-1 mx-0.5 rounded
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
      contentEditable={false}>
      <FiFilm className="w-3 h-3" />
    </span>
  );
}

function BreakChip({ editor, nodeKey, mentionData, style, selected }) {
  const [duration, setDuration] = useState(mentionData?.duration ?? 1);

  const handleDurationChange = useCallback(
    (e) => {
      const val = Math.max(0, parseFloat(e.target.value) || 0);
      setDuration(val);
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node) {
          const writable = node.getWritable();
          writable.__mentionData = { ...writable.__mentionData, duration: val };
        }
      });
    },
    [editor, nodeKey],
  );

  return (
    <span
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
      contentEditable={false}>
      <FaPause className="w-2.5 h-2.5 opacity-70" />
      <input
        type="number"
        min="0"
        step="0.5"
        value={duration}
        onChange={handleDurationChange}
        className="w-5 bg-transparent text-center text-[12px] outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        contentEditable={false}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
    </span>
  );
}

function EmotionChip({ editor, nodeKey, mentionData, style, selected }) {
  const [emotion, setEmotion] = useState(mentionData?.emotion ?? "");
  const inputRef = useRef(null);
  const measureRef = useRef(null);
  const [inputWidth, setInputWidth] = useState(40);

  useEffect(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    if (measureRef.current) {
      const measured = measureRef.current.scrollWidth + 4;
      setInputWidth(Math.max(40, Math.min(measured, 200)));
    }
  }, [emotion]);

  const handleEmotionChange = useCallback(
    (e) => {
      const val = e.target.value;
      setEmotion(val);
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node) {
          const writable = node.getWritable();
          writable.__mentionData = { ...writable.__mentionData, emotion: val };
        }
      });
    },
    [editor, nodeKey],
  );

  return (
    <span
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
      contentEditable={false}>
      <TbMoodSmile className="w-3 h-3 opacity-70" />
      <span
        ref={measureRef}
        className="absolute invisible whitespace-pre text-xs"
        aria-hidden="true">
        {emotion || "emotion"}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={emotion}
        onChange={handleEmotionChange}
        placeholder="emotion"
        style={{ width: inputWidth }}
        className="bg-transparent text-center text-xs outline-none placeholder:opacity-40"
        contentEditable={false}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
    </span>
  );
}

function CharacterChip({
  editor,
  nodeKey,
  mentionId,
  objectId,
  style,
  selected,
}) {
  const { show } = useShow();
  const { destinationFolder, requestGeneration } = useScriptAssets();
  const { runTaskAndPoll, getTask } = useKagamiBridge();
  const [generating, setGenerating] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const fetchedRef = useRef(false);

  const character = show?.characters?.find((c) => c._id === mentionId);
  const name = character?.name || mentionId;
  const thumbnailUrl = character?.thumbnail_url;
  const voiceId =
    character?.voice_ids?.find((v) => v.language === "en")?.voice_id ||
    character?.voice_ids?.[0]?.voice_id;
  // The generator panel keys its character dropdown by the first registered
  // voice id, so use that here to make the character pre-select correctly.
  const presetVoiceId = character?.voice_ids?.[0]?.voice_id;

  // Lazy fetch the most recent audio generation for this mention's group
  // when the chip first scrolls into view.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !objectId || !show?._id) return;
    if (fetchedRef.current) return;

    const fetchLatest = async () => {
      if (fetchedRef.current) return;
      fetchedRef.current = true;
      try {
        const params = new URLSearchParams({
          group: objectId,
          types: "audio",
          size: "1",
          page: "1",
        });
        const res = await apiFetch(
          `/api/shows/${show._id}/generations?${params}`,
        );
        if (!res.ok) return;
        const json = await res.json();
        const latest = json?.data?.[0];
        if (latest?.url) setAudioUrl(latest.url);
      } catch (err) {
        console.warn("Failed to fetch latest voice generation:", err);
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      fetchLatest();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          fetchLatest();
        }
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [objectId, show?._id]);

  const handleGenerate = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (generating) return;

      let speechText = "";
      editor.getEditorState().read(() => {
        const node = $getNodeByKey(nodeKey);
        if (node) speechText = $extractSpeechAfterMention(node);
      });

      if (!speechText) {
        console.warn("No speech text found after character mention");
        return;
      }
      if (!voiceId) {
        console.warn("No voice ID found for character", name);
        return;
      }

      try {
        setGenerating(true);
        const result = await runTaskAndPoll({
          modelId: VOICE_MODEL_ID,
          showId: show?._id,
          ...(objectId ? { group: objectId } : {}),
          generate_assets: true,
          destination_folder: destinationFolder || null,
          payload: {
            prompt: speechText,
            voiceId,
            // voiceSettings: {} // placeholder - falls back to model defaults
          },
        });

        let url = result?.payload?.voiceUrl;
        if (!url && result?._id) {
          const task = await getTask(result._id);
          url = task?.payload?.voiceUrl;
        }

        if (!url) {
          console.warn(
            "Voice generation completed but no audio URL was returned",
          );
          return;
        }

        setAudioUrl(url);
      } catch (err) {
        console.error("Voice generation failed:", err);
      } finally {
        setGenerating(false);
      }
    },
    [
      editor,
      nodeKey,
      objectId,
      voiceId,
      name,
      show?._id,
      generating,
      destinationFolder,
      runTaskAndPoll,
      getTask,
    ],
  );

  // Reveal the Assets generator panel and preset it with the ElevenLabs TTS
  // model, the speech that follows this mention, and the character's voice —
  // leaving the actual "Generate" to the user in the panel.
  const handleOpenGenerator = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      let speechText = "";
      editor.getEditorState().read(() => {
        const node = $getNodeByKey(nodeKey);
        if (node) speechText = $extractSpeechAfterMention(node);
      });

      requestGeneration({
        modelId: VOICE_MODEL_ID,
        settings: {
          prompt: speechText || "",
          ...(presetVoiceId ? { voiceId: presetVoiceId } : {}),
        },
      });
    },
    [editor, nodeKey, presetVoiceId, requestGeneration],
  );

  const handlePlayToggle = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!audioUrl) return;

      try {
        const existing = audioRef.current;
        if (existing && !existing.paused && !existing.ended) {
          // Stop: pause and rewind to the start.
          existing.pause();
          try {
            existing.currentTime = 0;
          } catch {
            /* ignore seek errors */
          }
          return;
        }

        if (
          existing &&
          existing.src &&
          existing.src.endsWith(audioUrl) === false
        ) {
          // URL changed (e.g. after re-generate) — drop the stale instance.
          existing.pause();
          audioRef.current = null;
        }

        let audio = audioRef.current;
        if (!audio) {
          audio = new Audio(audioUrl);
          audio.addEventListener("play", () => setPlaying(true));
          audio.addEventListener("playing", () => setPlaying(true));
          audio.addEventListener("pause", () => setPlaying(false));
          audio.addEventListener("ended", () => setPlaying(false));
          audioRef.current = audio;
        } else {
          // Always start from the beginning on a fresh play.
          try {
            audio.currentTime = 0;
          } catch {
            /* ignore seek errors */
          }
        }

        await audio.play();
      } catch (err) {
        console.error("Audio playback failed:", err);
        setPlaying(false);
      }
    },
    [audioUrl],
  );

  // If a new audio URL comes in (regenerate / lazy fetch), invalidate the
  // previously-cached <audio> instance so the next toggle uses the new one.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
    }
  }, [audioUrl]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const canPlay = !!audioUrl && !generating;

  return (
    <span
      ref={containerRef}
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center gap-2 rounded-md text-xs font-semibold overflow-hidden pr-2
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
      contentEditable={false}>
      {thumbnailUrl && (
        <img src={thumbnailUrl} alt={name} className="w-7 h-7 object-cover" />
      )}
      <span>{name}</span>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleOpenGenerator}
        title="Generate voice"
        className="px-1 py-0.5 self-stretch text-secondary-foreground/80 hover:text-secondary-foreground hover:bg-secondary-foreground/10 cursor-pointer flex items-center justify-center">
        <LuSparkles className="w-2.5 h-2.5" />
      </button>
    </span>
  );
}

function CompactCharacterChip({ mentionId, style, selected }) {
  const { show } = useShow();
  const character = show?.characters?.find((c) => c._id === mentionId);
  const name = character?.name || mentionId;
  return (
    <span
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
      contentEditable={false}>
      <FiUser className="w-3 h-3 opacity-70" />
      <span>{name}</span>
    </span>
  );
}

function ArtifactMentionChip({
  name,
  artifactId,
  style,
  selected,
  Icon = LuSparkles,
}) {
  const { loadArtifact } = useContext(MentionChipContext);

  // Double-click loads the referenced artifact into the panel's artifact-edit
  // mode — the same place picking it from the Load Artifact modal lands. No-op
  // if the caller hasn't wired loading (e.g. the script editor) or the artifact
  // can't be resolved (list not loaded yet, or it was deleted).
  const handleDoubleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      loadArtifact?.(artifactId);
    },
    [artifactId, loadArtifact],
  );

  return (
    <span
      id={`ArtifactMentionChip-${artifactId}`}
      onDoubleClick={handleDoubleClick}
      title="Double-click to load this artifact"
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-pointer select-none`}
      contentEditable={false}>
      <Icon className="w-3 h-3 opacity-70" />
      <span>{name}</span>
    </span>
  );
}

export const ASSET_MEDIA_ICONS = {
  image: FiImage,
  video: FiFilm,
  audio: FiMusic,
};

// Keeps the asset chip a predictable width regardless of how long the asset's
// name is — the full name is still available via the chip's `title` tooltip.
const ASSET_CHIP_NAME_MAX_LENGTH = 10;

function truncateAssetName(name) {
  if (!name || name.length <= ASSET_CHIP_NAME_MAX_LENGTH) return name;
  return `${name.slice(0, ASSET_CHIP_NAME_MAX_LENGTH)}…`;
}

// A dropped asset is identified by `ASSET_MENTION_ID_ATTRIBUTE` so the drop
// handler can tell it is over an existing asset chip and which asset that is.
export const ASSET_MENTION_ID_ATTRIBUTE = "data-asset-mention-id";

function AssetChip({
  assetId,
  url,
  thumbnailUrl: inlineThumbnailUrl,
  name,
  mediaType,
  style,
  selected,
}) {
  const { getAsset, ensureAssets } = useEditorAssets();
  const { openModal } = useModal();
  const catalogued = getAsset(assetId);

  // The store is seeded on drop, but a chip restored from a serialized value
  // arrives with nothing behind it — ask for the record once it is on screen.
  // An inline asset (no assetId — e.g. a Scene3D reference image) has no
  // catalogued record to fetch; it already carries its own url/thumbnail.
  useEffect(() => {
    if (!assetId || catalogued) return;
    ensureAssets([assetId]);
  }, [assetId, catalogued, ensureAssets]);

  const asset =
    catalogued ||
    (url ? { url, thumbnail_url: inlineThumbnailUrl, mediaType, name } : null);
  const thumbnailUrl =
    asset?.thumbnail_url || (asset?.mediaType === "image" ? asset?.url : null);
  const Icon = ASSET_MEDIA_ICONS[mediaType] || FiImage;

  const handlePreview = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      // AssetPreviewModal expects a catalogued Asset record (paging, _id,
      // ...) — an inline asset has no such record to open there.
      if (!catalogued) return;
      openModal(<AssetPreviewModal asset={catalogued} />);
    },
    [catalogued, openModal],
  );

  const chip = (
    <span
      id={`AssetMentionChip-${assetId || url}`}
      {...(assetId ? { [ASSET_MENTION_ID_ATTRIBUTE]: assetId } : {})}
      onClick={handlePreview}
      onMouseDown={(event) => event.preventDefault()}
      // Native title is the fallback for assets with no thumbnail to blow up in
      // a tooltip (audio, or still-loading records) — a thumbnail gets the richer
      // image tooltip below instead, so the two never show at once.
      title={thumbnailUrl ? undefined : catalogued ? `Open ${name}` : name}
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center gap-1 mx-0.5 rounded text-xs font-medium overflow-hidden
                ${style.bg} ${style.border} ${style.text} border select-none
                ${catalogued ? "cursor-pointer" : "cursor-default"}`}
      contentEditable={false}>
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={name}
          draggable={false}
          className="w-4 h-4 object-cover shrink-0"
        />
      ) : (
        <Icon className="w-3 h-3 opacity-70" />
      )}
      <span>{truncateAssetName(name)}</span>
    </span>
  );

  if (!thumbnailUrl) return chip;

  return (
    // Delayed so the preview doesn't flash up while just moving the cursor
    // across a line of chips — shadcn's Tooltip defaults to no delay at all.
    // Must go on <Tooltip>, not a wrapping <TooltipProvider>: Tooltip nests
    // its own TooltipProvider internally, which is the one Radix actually
    // reads and always shadowed an outer one with its own delayDuration={0}.
    <Tooltip delayDuration={400}>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent className="p-0">
        <img
          src={thumbnailUrl}
          alt={name}
          draggable={false}
          className="w-50 h-auto border border-primary rounded-sm object-cover"
        />
      </TooltipContent>
    </Tooltip>
  );
}

function DescriptionChip({ style, selected }) {
  return (
    <span
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
      contentEditable={false}>
      <FiFileText className="w-3 h-3 opacity-70" />
      <span>Description</span>
    </span>
  );
}

export default function MentionComponent({
  mentionType,
  mentionId,
  mentionName,
  mentionData,
  nodeKey,
  objectId,
}) {
  const [editor] = useLexicalComposerContext();
  const { compactCharacter } = useContext(MentionChipContext);
  const style = getStyle(mentionType, mentionId, mentionData);
  const selected = useMentionSelected(editor, nodeKey);

  if (mentionType === "character") {
    return compactCharacter ? (
      <CompactCharacterChip
        mentionId={mentionId}
        style={style}
        selected={selected}
      />
    ) : (
      <CharacterChip
        editor={editor}
        nodeKey={nodeKey}
        mentionId={mentionId}
        objectId={objectId}
        style={style}
        selected={selected}
      />
    );
  }

  if (mentionType === "artifact") {
    let Icon = LuSparkles;
    if (isCharacterArtifact(mentionData)) Icon = FiUser;
    else if (isLocationArtifact(mentionData)) Icon = LuMapPin;
    else if (isPropArtifact(mentionData)) Icon = LuBox;

    return (
      <ArtifactMentionChip
        name={mentionName}
        artifactId={mentionId}
        style={style}
        selected={selected}
        Icon={Icon}
      />
    );
  }

  if (mentionType === "asset") {
    return (
      <AssetChip
        assetId={mentionId}
        url={mentionData?.url}
        thumbnailUrl={mentionData?.thumbnail_url}
        name={mentionName}
        mediaType={mentionData?.mediaType}
        style={style}
        selected={selected}
      />
    );
  }

  if (mentionId === "segment") {
    return <SegmentChip style={style} selected={selected} />;
  }

  if (mentionId === "scene") {
    return <SceneChip style={style} selected={selected} />;
  }

  if (mentionId === "break") {
    return (
      <BreakChip
        editor={editor}
        nodeKey={nodeKey}
        mentionData={mentionData}
        style={style}
        selected={selected}
      />
    );
  }

  if (mentionId === "emotion") {
    return (
      <EmotionChip
        editor={editor}
        nodeKey={nodeKey}
        mentionData={mentionData}
        style={style}
        selected={selected}
      />
    );
  }

  if (mentionId === "description") {
    return <DescriptionChip style={style} selected={selected} />;
  }

  return (
    <span
      style={selectionRingStyle(selected)}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium
                ${style.bg} ${style.border} ${style.text} border cursor-default select-none`}
      contentEditable={false}>
      <span>{mentionName}</span>
    </span>
  );
}
